import { venueDistanceMiles } from 'utils/shared/geo/venueDistanceMiles';
import { useGeoLocation } from 'hooks/useGeoLocation';
import { useModal } from 'hooks/useModal';
import PlusPlain from 'svg/PlusPlain';
import type { VenueAddressWithCity } from 'screens/ListGame/types';
import { toVenueAddress } from 'screens/ListGame/utils';
import { ButtonText } from 'components/Button';
import LocationCard from 'components/Find/LocationCard';
import AddVenue from 'components/forms/AddVenue';
import type { AddVenueResult } from 'components/forms/AddVenue/props';
import classNames from 'styles/utils/classNames';

interface Props {
  venues: VenueAddressWithCity[];
  onChange: (venues: VenueAddressWithCity[]) => void;
  /** Onboarding bleeds the strip past the page gutters; a modal has none to bleed into. */
  isFullBleed?: boolean;
}

/** Where the player usually plays — a removable strip of venue cards plus a picker. */
export default function PreferredVenuesField({ venues, onChange, isFullBleed = false }: Props) {
  const { position } = useGeoLocation();
  const { isOpen, openModal, closeModal } = useModal();

  const addVenue = (venue: AddVenueResult) => {
    const mapped = toVenueAddress(venue);
    if (!venues.some((added) => added.id === mapped.id)) {
      onChange([mapped, ...venues]);
    }
    closeModal();
  };

  return (
    <>
      {venues.length > 0 && (
        <div
          className={classNames(
            'flex gap-ds-md overflow-x-auto pb-1',
            isFullBleed && '-mx-4 px-4 sm:-mx-6 sm:px-6 md:mx-0 md:px-0',
          )}
        >
          {venues.map((venue) => (
            <LocationCard
              key={venue.id}
              venue={{
                name: venue.title ?? '',
                imageUrl: venue.images?.[0],
                isIndoor: (venue.indoorCourtCount ?? 0) > 0,
                distanceMiles: venueDistanceMiles(position, venue),
                primarySport: venue.primarySport,
              }}
              onRemove={() => onChange(venues.filter((entry) => entry.id !== venue.id))}
            />
          ))}
        </div>
      )}

      <ButtonText
        size="sm"
        onClick={() => openModal()}
        className="flex items-center gap-ds-xs self-start !px-0 text-color-text-brand"
      >
        <PlusPlain className="h-4 w-4 shrink-0" /> Add location
      </ButtonText>

      <AddVenue
        title="Add location"
        isOpen={isOpen}
        onClose={closeModal}
        onSubmit={addVenue}
        onSubmitManualAddress={() => closeModal()}
      />
    </>
  );
}
